import { useMemo } from "react";
import { Button } from "@/components/common/Button";
import {
  SERVICE_KEYS,
  useOnboardingStore,
  type FieldValidation,
  type ServiceKey,
} from "@/stores/onboardingStore";

/**
 * Fifth step — the shared OpenSearch credentials for the program database.
 *
 * Admins only, and skipped entirely when the values are already present —
 * which after provisioning is the normal case. (Web research needs no
 * Perplexity key; it runs through OpenRouter's sonar models. The Nbyula
 * gateway key left this screen on 2026-07-29: it is per-employee and minted
 * server-to-server, so a typed one would make the user act as whoever owns
 * it.)
 */

interface FieldConfig {
  key: ServiceKey;
  label: string;
  hint: string;
  placeholder: string;
  type: "secret" | "text";
}

const FIELDS: FieldConfig[] = [
  {
    key: "opensearch_username",
    label: "OpenSearch username",
    hint: "Nbyula program database — provided by engineering.",
    placeholder: "admin",
    type: "text",
  },
  {
    key: "opensearch_password",
    label: "OpenSearch password",
    hint: "Verified against the username above.",
    placeholder: "••••••••",
    type: "secret",
  },
];

export function ServicesStep() {
  const { services, servicesValidation, setServiceValue, verifyService, goNext } =
    useOnboardingStore();

  const allVerified = useMemo(
    () => SERVICE_KEYS.every((k) => servicesValidation[k].kind === "verified"),
    [servicesValidation],
  );

  return (
    <div className="w-full">
      <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.01em] text-space">
        Connect the tools OmniWerk uses
      </h1>
      <p className="mt-1.5 text-[14px] leading-relaxed text-space-50">
        These are the keys the agent calls behind the scenes — research,
        program search, and production data. Engineering will have given
        them to you.
      </p>

      <div className="mt-6 space-y-5">
        {FIELDS.map((field) => (
          <ServiceField
            key={field.key}
            field={field}
            value={services[field.key]}
            validation={servicesValidation[field.key]}
            onChange={(v) => setServiceValue(field.key, v)}
            onVerify={() => verifyService(field.key)}
          />
        ))}
      </div>

      <div className="mt-7 flex justify-end">
        <Button
          variant="primary"
          size="md"
          onClick={goNext}
          disabled={!allVerified}
        >
          Continue
        </Button>
      </div>

      {!allVerified && (
        <p className="mt-3 text-right text-[12px] font-medium tracking-[0.04em] text-space-40">
          Verify each field to continue.
        </p>
      )}
    </div>
  );
}

interface ServiceFieldProps {
  field: FieldConfig;
  value: string;
  validation: FieldValidation;
  onChange: (value: string) => void;
  onVerify: () => Promise<{ verified: boolean; message: string }>;
}

function ServiceField({ field, value, validation, onChange, onVerify }: ServiceFieldProps) {
  const verifying = validation.kind === "verifying";
  const verified = validation.kind === "verified";

  return (
    <div className="space-y-1.5">
      <label className="block text-[14px] font-medium text-space">
        {field.label} <span className="text-hydra">*</span>
      </label>
      <div className="flex gap-2">
        <input
          type={field.type === "secret" ? "password" : "text"}
          className="flex-1 rounded-lg border border-space-30 px-3 py-2 font-mono text-[14px] text-space placeholder:text-space-40 focus:border-jove focus:outline-none focus:ring-2 focus:ring-jove-60"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          autoComplete="off"
          spellCheck={false}
        />
        <Button
          variant={verified ? "secondary" : "primary"}
          size="sm"
          onClick={onVerify}
          disabled={!value.trim() || verifying}
          loading={verifying}
        >
          {verified ? "Re-check" : "Verify"}
        </Button>
      </div>
      <ValidationLine hint={field.hint} validation={validation} />
    </div>
  );
}

function ValidationLine({
  hint,
  validation,
}: {
  hint: string;
  validation: FieldValidation;
}) {
  if (validation.kind === "verified") {
    return (
      <p className="flex items-center gap-1 text-[12px] font-medium tracking-[0.04em] text-emerald-600">
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        {validation.message}
      </p>
    );
  }
  if (validation.kind === "failed") {
    return (
      <p className="flex items-center gap-1 text-[12px] font-medium tracking-[0.04em] text-hydra">
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        {validation.message}
      </p>
    );
  }
  return <p className="text-[12px] font-medium tracking-[0.04em] text-space-50">{hint}</p>;
}
